import { useCallback, useEffect, useState } from "react";
import { HiOutlinePlus, HiOutlineTrash, HiOutlineUserGroup } from "react-icons/hi2";
import toast from "react-hot-toast";
import { courseAssignmentsAPI } from "../../services/api";
import "./CourseRules.css";

/* What each kind of rule is called, per organisation */
const KINDS = {
  everyone: "Every guest with a confirmed stay",
  type: "A retreat type",
  date: "One retreat date",
  block: "A retreat block",
  cohort: "A cohort",
  category: "A category",
  person: "One person",
};
const RULE_LABEL = { everyone: "Everyone", type: "Retreat type", date: "Date", block: "Block", cohort: "Cohort", category: "Category", person: "Person" };

const whenLabel = (r) =>
  r.opens === "before_start" ? `Opens ${r.open_days} day${r.open_days === 1 ? "" : "s"} before the retreat starts`
    : r.opens === "after_end" ? `Opens ${r.open_days} day${r.open_days === 1 ? "" : "s"} after the retreat ends`
      : "Open straight away";

const fmtDay = (d) => (d ? new Date(String(d).replace(" ", "T")).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

const EMPTY = { scope: "", scope_id: "", scope_value: "", opens: "now", open_days: 14, needs_clearance: false };

/**
 * Who gets one course, and how they're getting on. The same panel in both
 * admins: Forge offers blocks, cohorts, categories and people; Makua
 * offers every confirmed guest, retreat types, dates, cohorts and people,
 * and can hold a course back until medical screening clears.
 */
export default function CourseRules({ courseId, canEdit, intro, onChange }) {
  const [rules, setRules] = useState(null);
  const [opts, setOpts] = useState(null);
  const [people, setPeople] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    Promise.all([courseAssignmentsAPI.list(courseId), courseAssignmentsAPI.enrolment(courseId)])
      .then(([r, e]) => { setRules(r.data.rules); setPeople(e.data.people); })
      .catch((err) => setError(err.message));
  }, [courseId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { courseAssignmentsAPI.options().then((r) => setOpts(r.data)).catch(() => setOpts({ scopes: [] })); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const isMakua = !!opts?.scopes?.includes("everyone");

  const targets = {
    type: opts?.types?.map((t) => ({ id: t.id, name: t.name })),
    date: opts?.dates?.map((d) => ({ id: d.id, name: `${d.name} · ${fmtDay(d.start)}` })),
    block: opts?.blocks?.map((b) => ({ id: b.id, name: `${b.name} · ${fmtDay(b.start)}` })),
    cohort: opts?.cohorts,
  }[form.scope];

  const ready = form.scope && (
    form.scope === "everyone" ||
    (form.scope === "person" && /\S+@\S+\.\S+/.test(form.scope_value)) ||
    (form.scope === "category" && form.scope_value) ||
    (targets && form.scope_id)
  );

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await courseAssignmentsAPI.add({ course_id: courseId, ...form, open_days: Number(form.open_days) || 0 });
      toast.success("Added.");
      setForm(EMPTY);
      load();
      onChange?.();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Stop giving this course to: ${r.target}? Progress already made is kept.`)) return;
    try { await courseAssignmentsAPI.remove(r.id); toast.success("Removed."); load(); onChange?.(); }
    catch (err) { toast.error(err.message); }
  };

  const finished = (people || []).filter((p) => p.complete).length;

  return (
    <div className="crl">
      <section className="crl__card" aria-labelledby={`crl-who-${courseId}`}>
        <h3 id={`crl-who-${courseId}`} className="crl__title">Who gets this course</h3>
        {intro && <p className="crl__intro">{intro}</p>}
        {error && <p className="crl__error" role="alert">{error}</p>}
        {rules && rules.length === 0 && <p className="crl__empty">No one yet. Add who it's for below.</p>}
        {rules && rules.length > 0 && (
          <ul className="crl__rules">
            {rules.map((r) => (
              <li key={r.id} className="crl__rule">
                <span className="crl__kind">{RULE_LABEL[r.scope]}</span>
                <span className="crl__target">{r.target}</span>
                <span className="crl__when">
                  {whenLabel(r)}
                  {r.needs_clearance && <span className="crl__chip">after medical clearance</span>}
                </span>
                {canEdit && (
                  <button type="button" className="crl__remove" onClick={() => remove(r)} aria-label={`Remove ${RULE_LABEL[r.scope]} ${r.target}`}>
                    <HiOutlineTrash />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {canEdit && opts && (
          <form className="crl__form" onSubmit={add}>
            <label className="crl__field">
              <span>Give it to</span>
              <select value={form.scope} onChange={(e) => setForm({ ...EMPTY, opens: form.opens, open_days: form.open_days, scope: e.target.value })}>
                <option value="">Choose…</option>
                {opts.scopes.map((s) => <option key={s} value={s}>{KINDS[s]}</option>)}
              </select>
            </label>

            {targets && (
              <label className="crl__field">
                <span>Which</span>
                <select value={form.scope_id} onChange={set("scope_id")}>
                  <option value="">Choose…</option>
                  {targets.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
            )}
            {form.scope === "category" && (
              <label className="crl__field">
                <span>Category</span>
                <select value={form.scope_value} onChange={set("scope_value")}>
                  <option value="">Choose…</option>
                  {(opts.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            )}
            {form.scope === "person" && (
              <label className="crl__field">
                <span>Email</span>
                <input type="email" value={form.scope_value} onChange={set("scope_value")} placeholder="name@example.com" />
              </label>
            )}

            {form.scope && (
              <fieldset className="crl__opens">
                <legend>When it opens</legend>
                <label><input type="radio" name={`opens-${courseId}`} checked={form.opens === "now"} onChange={() => setForm((f) => ({ ...f, opens: "now" }))} /> Straight away</label>
                <label><input type="radio" name={`opens-${courseId}`} checked={form.opens === "before_start"} onChange={() => setForm((f) => ({ ...f, opens: "before_start" }))} /> Before the retreat starts</label>
                <label><input type="radio" name={`opens-${courseId}`} checked={form.opens === "after_end"} onChange={() => setForm((f) => ({ ...f, opens: "after_end" }))} /> After it ends</label>
                {form.opens !== "now" && (
                  <label className="crl__days">
                    <input type="number" min="0" max="365" value={form.open_days} onChange={set("open_days")} aria-label="Days" /> days {form.opens === "before_start" ? "before" : "after"}
                  </label>
                )}
              </fieldset>
            )}

            {form.scope && isMakua && (
              <label className="crl__check">
                <input type="checkbox" checked={form.needs_clearance} onChange={set("needs_clearance")} />
                Only once their medical screening has cleared
              </label>
            )}

            <button type="submit" className="a-btn a-btn--rust crl__add" disabled={!ready || busy}>
              <HiOutlinePlus /> {busy ? "Adding…" : "Add"}
            </button>
          </form>
        )}
      </section>

      <section className="crl__card" aria-labelledby={`crl-reach-${courseId}`}>
        <h3 id={`crl-reach-${courseId}`} className="crl__title">
          <HiOutlineUserGroup aria-hidden="true" /> Who it reaches
          {people && people.length > 0 && <span className="crl__count">{people.length} {people.length === 1 ? "person" : "people"} · {finished} finished</span>}
        </h3>
        {people && people.length === 0 && <p className="crl__empty">No one yet.</p>}
        {people && people.length > 0 && (
          <div className="crl__table-wrap">
            <table className="crl__table">
              <thead><tr><th>Name</th><th>Through</th><th>Progress</th><th>Last activity</th></tr></thead>
              <tbody>
                {people.map((p) => (
                  <tr key={p.personId || p.email}>
                    <td><span className="crl__name">{p.name}</span><span className="crl__email">{p.email}</span></td>
                    <td>{p.via.length ? p.via.map((v) => RULE_LABEL[v]).join(", ") : "Their access level"}</td>
                    <td>
                      <span className="crl__bar" aria-hidden="true"><span style={{ width: `${p.total ? (100 * p.done) / p.total : 0}%` }} /></span>
                      <span className={p.complete ? "crl__done" : ""}>{p.complete ? "Finished" : `${p.done} of ${p.total}`}</span>
                    </td>
                    <td>{p.lastActivity ? fmtDay(p.lastActivity) : "Not started"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
